<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />

        <v-flex xs4>
          <v-autocomplete
            v-if="hasPermissions(['outlet_select'])"
            v-model="outletId"
            :label="`${$t('Outlet')} *`"
            hide-details
            :items="outletsList"
            @input="outletChanged()"
            data-vv-name="outlet"
            :error="errors.has('outlet')"
            v-validate="'required'"
          />
        </v-flex>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.purchase_id }}</td>-->
          <td>{{ props.item.bill_auto_number }}</td>
          <td>{{ props.item.supplier_invoice_number }}</td>
          <td>{{ props.item.outlet_name }}</td>
          <td>{{ props.item.vender_name }}</td>
          <td>{{ props.item.grand_total_amount | currency }}</td>
          <td>{{ props.item.bill_date }}</td>
          <td>{{ props.item.cash_or_opening_balance }}</td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="props.item.bill_status === 'Open' ? 'error' : 'success'"
                text-color="white"
                class="no-select"
              >
                <v-icon>{{ props.item.bill_status === 'Open' ? 'fa-lock-open' : 'fa-lock' }}</v-icon>
              </v-chip>
              <span>{{ props.item.bill_status === 'Open' ? $t('Open') : $t('Close') }}</span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="
                  props.item.payment_status === null && props.item.cash_or_opening_balance === 'Opening Balance'
                    ? 'primary'
                    : props.item.payment_status === 'Paid'
                    ? 'success'
                    : props.item.payment_status === 'Paid Partially'
                    ? 'warning'
                    : 'error'
                "
                text-color="white"
                class="no-select"
              >
                <v-icon>{{
                  props.item.payment_status === 'Paid' || props.item.payment_status === 'Paid Partially'
                    ? 'attach_money'
                    : props.item.payment_status === null && props.item.cash_or_opening_balance === 'Opening Balance'
                    ? 'monetization_on'
                    : 'money_off'
                }}</v-icon>
              </v-chip>
              <span>{{
                props.item.payment_status === 'Paid'
                  ? $t('Paid')
                  : props.item.payment_status === 'Paid Partially'
                  ? $t('Paid Partially')
                  : props.item.payment_status === null && props.item.cash_or_opening_balance === 'Opening Balance'
                  ? $t('Opening Balance')
                  : $t('Not Paid')
              }}</span>
            </v-tooltip>
          </td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['purchases_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.purchase_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Purchase's information`) }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <view-purchase :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    viewPurchase: () => import('@/views/accounting/purchases/viewPurchase.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      outletId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'purchase_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'purchase_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Bill Number'), value: 'bill_auto_number' },
        {
          text: this.$t('Supplier Invoice Number'),
          value: 'supplier_invoice_number'
        },
        { text: this.$t('Outlet'), value: 'outlet_name' },
        { text: this.$t('Supplier'), value: 'vender_name' },
        {
          text: `${this.$t('Grand Total')} (${localStorage.systemCurrency})`,
          value: 'grand_total_amount'
        },
        { text: this.$t('Bill Date'), value: 'bill_date' },
        { text: this.$t('Payment Type'), value: 'cash_or_opening_balance' },
        { text: this.$t('Bill Status'), value: 'bill_status' },
        { text: this.$t('Payment Status'), value: 'payment_status' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Purchases Report'),
      icon: 'fa-chart-line'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }

    this.fetchOutletList()
  },

  computed: {
    ...mapGetters({
      resultData: 'purchases/getPurchaseList',
      outletsList: 'outlets/getFullReportOutletsList',
      authUserData: 'auth/getAuthUserData'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'purchases/updateReportPurchaseList',
      fetchPurchaseInfo: 'purchases/fetchPurchaseInfo',
      fetchOutletList: 'outlets/setFullOutletsList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['purchases_show'])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`report_purchases/${this.outletId}`, {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    outletChanged() {
      this.updateDatatableResult()
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        ...this.pagination,
        search: this.search,
        id: this.outletId
      }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true

      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchPurchaseInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    }
  }
}
</script>

<style scoped />
